import { MovieContentList } from './MovieContentList';
import { getContentGroups, getContents } from '@/api';
import { Device } from '@/classes';
import { useExtendFocusable } from '@/hooks/useExtendFocusable';
import { CardRowSkeleton } from '@/shared/ui';
import { Content, ContentGroup } from '@/types';
import { isFocusScrollSuppressed, scrollElementTo } from '@/utils';
import { FocusContext } from '@noriginmedia/norigin-spatial-navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type MovieContentGroupListProps = {
  sourcePage: string;
  containerClassName?: string;
  headerClassName?: string;
  header?: React.ReactNode;
};

// 한 줄에 받아오는 카드 수. (기존 MovieContentList 의 load(0, 200) 과 같다)
const MOVIE_ROW_LIMIT = 200;
// 데이터 전에 잡아둘 줄 수·줄당 카드 수.
const SKELETON_ROW_COUNT = 2;
const SKELETON_CARD_COUNT = 5;

type MovieRow = { contentGroup: ContentGroup; contents: Array<Content> };

export const MovieContentGroupList = ({
  sourcePage,
  containerClassName,
  headerClassName,
  header,
}: MovieContentGroupListProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [rows, setRows] = useState<Array<MovieRow>>([]);

  const [loading, setLoading] = useState(false);

  // 모든 줄의 카드를 하나의 컨테이너 아래 형제로 묶는다.
  // 줄마다 컨테이너를 두면 줄별 마지막 포커스가 기억되어 아래/위 키가 바로 밑(위) 카드로 가지 않는다.
  // ! 카드가 사라질 때 autoRestoreFocus 가 되돌릴 부모도 이 컨테이너가 맡는다.
  //   (부모가 ROOT 면 복구 대상이 없어 포커스가 사라진다)
  // ! trackChildren 은 쓰지 않는다. 포커스 진입/이탈마다 리렌더되어 콘텐츠를 재요청하게 된다.
  // ! focusable: false — 컨테이너 자신은 절대 포커스 대상이 되지 않게 한다.
  //   이 화면은 줄 목록을 먼저 받고 각 줄이 카드를 따로 받는 2단계 로딩이라, 그 사이에는
  //   컨테이너에 포커스 가능한 자식이 없다. 이때 포커스가 들어오면 크기만 큰 빈 영역이라 화면에서 사라진다.
  //   자식(카드)로의 진입과 autoRestoreFocus 는 컨테이너의 focusable 여부와 무관하게 동작한다.
  const { ref, focusKey } = useExtendFocusable({
    isContainer: true,
    saveLastFocusedChild: true,
    autoRestoreFocus: true,
    focusable: false,
  });

  // ! 줄 목록 → 줄별 카드의 2단계 로딩을 한 번에 끝낸다.
  // ! 따로 받으면 페이지 스켈레톤 → 줄 스켈레톤 → 카드 순으로 화면이 두 번 바뀌어 깜빡인다.
  const loadContentGroups = useCallback(async () => {
    try {
      setLoading(true);
      const contentGroups = await getContentGroups({
        type_in: ['SERIES'],
        language: Device.language,
      });

      const contentsPerGroup = await Promise.all(
        contentGroups.map((contentGroup) =>
          getContents({
            type: 'VIDEO',
            contentGroupId: contentGroup.id,
            thumbnailType: 'VERTICAL',
            language: Device.language,
            resolution: 'P60',
            offset: 0,
            limit: MOVIE_ROW_LIMIT,
          })
            .then(({ contents }) => contents)
            .catch((e) => {
              console.error(e);
              return [] as Array<Content>;
            }),
        ),
      );

      setRows(contentGroups.map((contentGroup, i) => ({ contentGroup, contents: contentsPerGroup[i] })));
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to load content groups', e);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const onFocused = useCallback(({ top }: { top: number }) => {
    if (isFocusScrollSuppressed('vertical')) {
      return;
    }

    const current = scrollRef.current;

    if (current) {
      scrollElementTo({ element: current, top: top - 200 });
    }
  }, []);

  const contentListComponents = useMemo(
    () =>
      rows.map(({ contentGroup, contents }) => (
        <MovieContentList
          sourcePage={sourcePage}
          key={contentGroup.id}
          contentGroup={contentGroup}
          contents={contents}
          onFocused={onFocused}
        />
      )),
    [rows, onFocused, sourcePage],
  );

  useEffect(() => {
    loadContentGroups();
  }, [loadContentGroups]);

  return (
    <main id="main-container" className={containerClassName} ref={scrollRef}>
      {/* Header */}
      <div className={headerClassName}>{header}</div>

      {/* Contents */}
      <div className="pt-[130px] w-[1764px]" ref={ref}>
        {loading && (
          <CardRowSkeleton
            rows={SKELETON_ROW_COUNT}
            cards={SKELETON_CARD_COUNT}
            cardClassName="w-[360px] h-[522px] mr-[20px] mb-[20px]"
          />
        )}
        {!loading && <FocusContext.Provider value={focusKey}>{contentListComponents}</FocusContext.Provider>}
      </div>
    </main>
  );
};
